<template>
    <div ref="companyDisplayModal">
        <!-- Mobile: custom drawer (original app drawer) -->
        <transition name="drawer-fade">
            <div
                v-if="isMobile && isDrawerOpen"
                class="drawer-overlay"
                data-testid="company-display-drawer-overlay"
                @click.self="close"
            >
                <div
                    class="drawer-panel-wrapper"
                    :style="drawerWrapperStyle"
                >
                    <div
                        class="drawer-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="company-display-modal-title"
                    >
                        <CompanyDisplaySettingsContent
                            :key="`drawer-${openKey}`"
                            :companies="companies"
                            :selected-company-ids="selectedCompanyIds"
                            :registered-company-ids="registeredCompanyIds"
                            @apply="handleApply"
                            @close="close"
                        />
                    </div>
                </div>
            </div>
        </transition>

        <!-- Desktop: pure Bootstrap 4 modal (popup) -->
        <div
            v-if="!isMobile"
            class="modal fade company-display-settings-modal"
            id="companyDisplayBootstrapModal"
            tabindex="-1"
            role="dialog"
            aria-labelledby="company-display-modal-title"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content company-display-modal-content">
                    <CompanyDisplaySettingsContent
                        :key="`modal-${openKey}`"
                        :companies="companies"
                        :selected-company-ids="selectedCompanyIds"
                        :registered-company-ids="registeredCompanyIds"
                        @apply="handleApply"
                        @close="close"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CompanyDisplaySettingsContent from './CompanyDisplaySettingsContent.vue';

export default {
    name: 'CompanyDisplaySettingsModal',
    components: {
        CompanyDisplaySettingsContent,
    },
    props: {
        companies: {
            type: Array,
            default: () => [],
        },
        selectedCompanyIds: {
            type: Array,
            default: () => [],
        },
        registeredCompanyIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isMobile: true,
            isDrawerOpen: false,
            openKey: 0,
            containerRect: null,
            _resizeHandler: null,
            _mobileQuery: null,
            _viewportChangeHandler: null,
        };
    },
    computed: {
        drawerWrapperStyle() {
            if (!this.isMobile || !this.containerRect) return {};
            return {
                position: 'absolute',
                left: `${this.containerRect.left}px`,
                bottom: 0,
                width: `${this.containerRect.width}px`,
                maxWidth: 'none',
            };
        },
    },
    methods: {
        updateViewport() {
            this.isMobile = this._mobileQuery ? this._mobileQuery.matches : window.innerWidth <= 767;
        },
        updateContainerRect() {
            if (!this.isMobile) return;
            const el = document.querySelector('.contact-container');
            if (el) {
                const rect = el.getBoundingClientRect();
                this.containerRect = { width: rect.width, left: rect.left };
            } else {
                this.containerRect = null;
            }
        },
        getBootstrapModal() {
            const el = document.getElementById('companyDisplayBootstrapModal');
            return el && window.jQuery ? window.jQuery(el) : null;
        },
        show() {
            this.updateViewport();
            this.openKey += 1;
            if (this.isMobile) {
                this.isDrawerOpen = true;
                this.lockBodyScroll();
                this.$nextTick(() => {
                    this.updateContainerRect();
                    this._resizeHandler = () => {
                        this.updateViewport();
                        this.updateContainerRect();
                    };
                    window.addEventListener('resize', this._resizeHandler);
                });
            } else {
                const $modal = this.getBootstrapModal();
                if ($modal && $modal.modal) {
                    $modal.modal('show');
                }
                this.$nextTick(() => {
                    this._resizeHandler = () => this.updateViewport();
                    window.addEventListener('resize', this._resizeHandler);
                });
            }
        },
        close() {
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
                this._resizeHandler = null;
            }
            if (this.isMobile) {
                this.isDrawerOpen = false;
                this.containerRect = null;
                this.unlockBodyScroll();
                this.$emit('hidden');
            } else {
                const $modal = this.getBootstrapModal();
                if ($modal && $modal.modal) {
                    $modal.modal('hide');
                }
            }
        },
        handleApply(ids) {
            this.$emit('apply', ids);
            this.close();
        },
        onBootstrapModalHidden() {
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
                this._resizeHandler = null;
            }
            this.unlockBodyScroll();
            this.$emit('hidden');
        },
        lockBodyScroll() {
            this._previousBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        },
        unlockBodyScroll() {
            document.body.style.overflow = this._previousBodyOverflow || '';
        },
    },
    mounted() {
        this._mobileQuery = window.matchMedia('(max-width: 767px)');
        this.updateViewport();
        this._viewportChangeHandler = () => this.updateViewport();
        this._mobileQuery.addEventListener('change', this._viewportChangeHandler);
        const $modal = this.getBootstrapModal();
        if ($modal) {
            $modal.on('show.bs.modal', () => this.lockBodyScroll());
            $modal.on('hidden.bs.modal', () => this.onBootstrapModalHidden());
        }
    },
    beforeDestroy() {
        if (this._mobileQuery && this._viewportChangeHandler) {
            this._mobileQuery.removeEventListener('change', this._viewportChangeHandler);
        }
        const $modal = this.getBootstrapModal();
        if ($modal) {
            $modal.off('show.bs.modal hidden.bs.modal');
        }
    },
};
</script>

<style scoped>
/* Drawer (mobile) */
.drawer-fade-enter-active,
.drawer-fade-leave-active {
    transition: opacity 0.2s ease;
}
.drawer-fade-enter-active .drawer-panel,
.drawer-fade-leave-active .drawer-panel {
    transition: transform 0.25s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
    opacity: 0;
}
.drawer-fade-enter-from .drawer-panel,
.drawer-fade-leave-to .drawer-panel {
    transform: translateY(100%);
}

.drawer-overlay {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
}

.drawer-panel-wrapper {
    width: 100%;
    max-width: 720px;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    cursor: default;
}

.drawer-panel {
    width: 100%;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    background-color: #E5E7EB;
    border-radius: 24px 24px 0 0;
    overflow: hidden;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

/* Bootstrap modal (desktop) */
.company-display-modal-content {
    background-color: transparent;
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
</style>
